import { useCallback, useRef, useState, useEffect } from "react";
import Attorney from "./icons/Attorney";
import { StyledAttorneySecondaryButton } from "./AttorneyServicesComponent";
import {
  BORDER_RADIUS_DEFAULT,
  COLOR_TERTIARY,
  FLEX_COLUMN,
  FLEX_ROW,
  HEADER_Z_INDEX,
  MOBILE_SCREEN,
} from "./theme";
import styled from "@emotion/styled";
import { LinkButton } from "./buttons/LinkButton";
import Chats from "./icons/Chats";
import Scales from "./icons/Scales";
import { IconSize } from "./theme";
import { IconButton } from "./buttons/IconButton";
import BubbleChevronDown from "./icons/BubbleChevronDown";
import BubbleChevronUp from "./icons/BubbleChevronUp";
import { useNavigate } from "react-router";
import CompletePaymentModal from "./layout/CompletePaymentModal";

const AttorneyServicesUnpaidContainer = styled.div`
  position: fixed;
  ${FLEX_COLUMN}
  top: 64px;
  right: 48px;
  gap: 16px;
  z-index: ${HEADER_Z_INDEX};
  border-radius: ${BORDER_RADIUS_DEFAULT};
  padding: 8px 32px;
  background-color: ${COLOR_TERTIARY};
  width: 100%;
  max-width: 176px;
  ${MOBILE_SCREEN} {
    right: auto;
    left: 50%;
    transform: translateX(-50%);
    max-width: calc(100vw - 80px);
  }
`;

export const StyledLinkButton = styled(LinkButton)`
  ${FLEX_ROW}
  gap: 8px;
  align-items: center;
  justify-content: flex-start;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

export interface AttorneyServicesUnpaidProps {
  isMobile: boolean;
  isLoading: boolean;
  stateName: string;
}
const AttorneyServicesUnpaid = ({
  isMobile,
  isLoading,
  stateName,
}: AttorneyServicesUnpaidProps) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        cardRef.current &&
        !cardRef.current.contains(event.target as Node) &&
        !buttonRef.current?.contains(event.target as Node) &&
        !modalRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setIsModalOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setIsOpen, setIsModalOpen, buttonRef]);

  const handleClick = () => {
    setIsModalOpen(true);
  };

  const checkout = useCallback(() => {
    setIsModalOpen(false);
    setIsOpen(false);
    void navigate("/payment");
  }, [navigate]);

  const goBack = useCallback(() => {
    setIsModalOpen(false);
    setIsOpen(false);
  }, []);

  if (isMobile) {
    return (
      <>
        <IconButton
          data-testid="attorney-services-button"
          ref={buttonRef}
          onClick={() => {
            setIsOpen(!isOpen);
          }}
        >
          <Attorney />
        </IconButton>
        {isOpen && (
          <AttorneyServicesUnpaidContainer ref={cardRef}>
            <StyledLinkButton onClick={handleClick}>
              <Chats size={IconSize.S} />
              Q&A
            </StyledLinkButton>
            <StyledLinkButton onClick={handleClick}>
              <Scales size={IconSize.S} />
              Representation
            </StyledLinkButton>
          </AttorneyServicesUnpaidContainer>
        )}
        {isModalOpen && (
          <CompletePaymentModal
            onConfirm={checkout}
            onCancel={goBack}
            componentRef={modalRef}
            stateName={stateName}
          />
        )}
      </>
    );
  }

  return (
    <>
      <StyledAttorneySecondaryButton
        data-testid="attorney-services-button"
        ref={buttonRef}
        style={{ opacity: isLoading ? 0 : 1 }}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
      >
        <Attorney />
        Attorney services
        {isOpen ? <BubbleChevronUp /> : <BubbleChevronDown />}
      </StyledAttorneySecondaryButton>
      {isOpen && (
        <AttorneyServicesUnpaidContainer ref={cardRef}>
          <StyledLinkButton onClick={handleClick}>
            <Chats size={IconSize.S} />
            Q&A
          </StyledLinkButton>
          <StyledLinkButton onClick={handleClick}>
            <Scales size={IconSize.S} />
            Representation
          </StyledLinkButton>
        </AttorneyServicesUnpaidContainer>
      )}
      {isModalOpen && (
        <CompletePaymentModal
          onConfirm={checkout}
          onCancel={goBack}
          componentRef={modalRef}
          stateName={stateName}
        />
      )}
    </>
  );
};

export default AttorneyServicesUnpaid;
